import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDatabase, ref, get, set, onValue, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { loadPageCustomFonts, loadUserCustomFonts, preloadFontsForMobile, injectCustomFont } from '../utils/customFontLoader';
import { initLivePageRichTextFixer, startLivePageRichTextWatcher } from '../utils/livePageRichTextFixer'; // ✅ Rich Text Fixer
import NewNavCreator from './WidgetEditComponents/NewNavCreator';
import ImageWidget from './WidgetTypes/ImageWidget';
import PopupManager from './PopupManager';
import './PagePublic.css';
import './WidgetTypes/ResponsiveFonts.css';           // ✅ KRITISCH: Mobile Media Queries
import './WidgetTypes/WidgetStyle.css';               // ✅ KRITISCH: Widget Base Styles
import './WidgetEditComponents/TextEffectsAnimations.css'; // ✅ KRITISCH: Text Effects & Animations
import './WidgetEditComponents/TipTapTextAnimations.css';  // ✅ KRITISCH: Element Animations (fadeInUp, elegantScale, etc.)
import EmptyPage from './EmptyPage';
import PageLoading from './PageLoading';
import CustomPageLoading from './CustomPageLoading';
import AccessVerification from './AccessVerification';
import HeroHeader from './WidgetTypes/HeroHeader';
import HiWidget from './WidgetTypes/HiWidget';
import VideoTeaser from './WidgetTypes/VideoTeaser';
import SimplisticHeader from './WidgetTypes/SimplisticHeader';
import SupremeTestimonial from './WidgetTypes/SupremeTestimonial';
import SimplisticQuote from './WidgetTypes/SimplisticQuote';
import MethodologyWidget from './WidgetTypes/MethodologyWidget';
import PersonalBrandingWidget from './WidgetTypes/PersonalBrandingWidget';
import SimplisticTestimonial from './WidgetTypes/SimplisticTestimonial';
import SimplisticServices from './WidgetTypes/SimplisticServices';
import BulletPointsWidget from './WidgetTypes/BulletPointsWidget';
import ComparisonWidget from './WidgetTypes/ComparisonWidget';
import ProductInfoWidget from './WidgetTypes/ProductInfoWidget';
import EventInfoWidget from './WidgetTypes/EventInfoWidget';
import CourseInfoWidget from './WidgetTypes/CourseInfoWidget';
import InfiniteBanner from './WidgetTypes/InfiniteBanner';
import QAWidget from './WidgetTypes/QAWidget';
import HolisticTestimonial from './WidgetTypes/HolisticTestimonial';
import BragBar from './WidgetTypes/BragBar';
import SpiritualFeatureBanner from './WidgetTypes/SpiritualFeatureBanner';
import FancyFooter from './WidgetTypes/FancyFooter';
import AchievementsBanner from './WidgetTypes/AchievementsBanner';
import SpiritualQuoteCard from './WidgetTypes/SpiritualQuoteCard';
import GalleryWidget from './WidgetTypes/GalleryWidget';
import { isCustomDomain, resolvePageForDomain } from '../utils/domainHelpers';
import { getSpaceBySpacename } from '../utils/usernameHelpers';
import { 
    loadPageFromFirestore, 
    loadCustomDomainPageFromFirestore,
    checkFirestoreSiteStatus 
} from '../utils/firestorePageLoader';

function PagePublic() {
  const { customRoute, username, pagename } = useParams();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [currentPageId, setCurrentPageId] = useState('');
  const [domainMapping, setDomainMapping] = useState(null);
  const [accessGranted, setAccessGranted] = useState(false);
  const [showAccessVerification, setShowAccessVerification] = useState(false);
  const [pageSource, setPageSource] = useState('unknown'); // Track data source
  const [versionInfo, setVersionInfo] = useState(null); // Track version info
  const [customMetaSettings, setCustomMetaSettings] = useState(null); // Custom meta for domains
  const [metaLoading, setMetaLoading] = useState(false); // Track meta loading state
  const [isMobileView, setIsMobileView] = useState(false); // ✅ NEW: Mobile detection for navigation
  const db = getDatabase();

  // Initialize empty meta tags for custom domains to prevent flicker
  useEffect(() => {
    const originalHost = window.__BC_ORIGINAL_HOST__ || 
                        new URLSearchParams(window.location.search).get('__bc_original_host');
    
    if (originalHost && !customMetaSettings) {
      // Set temporary empty meta tags to prevent BonusClub branding flicker
      document.title = 'Loading...';
      
      // Clear default meta tags immediately
      const setEmptyMetaTag = (selector) => {
        const meta = document.querySelector(selector);
        if (meta) {
          meta.setAttribute('content', '');
        }
      };
      
      setEmptyMetaTag('meta[name="description"]');
      setEmptyMetaTag('meta[property="og:title"]');
      setEmptyMetaTag('meta[property="og:description"]');
      setEmptyMetaTag('meta[property="og:site_name"]');
      
      console.log('🏷️ PagePublic: Cleared default meta tags for custom domain:', originalHost);
    }
  }, []); // Run once on mount

  // ✅ Mobile Detection for Navigation
  useEffect(() => {
    const checkMobile = () => {
      const isMobile = window.innerWidth <= 768;
      setIsMobileView(isMobile);
    };
    
    // Initial check
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Apply custom meta tags directly to DOM
  const applyCustomMetaTags = (metaSettings) => {
    const originalHost = window.__BC_ORIGINAL_HOST__ || 
                        new URLSearchParams(window.location.search).get('__bc_original_host');
    
    if (!originalHost || !metaSettings) {
      return;
    }

    const pageTitle = metaSettings.siteTitle || 
                     (pageData?.name ? `${pageData.name} - ${metaSettings.siteName}` : metaSettings.siteName) ||
                     'Professional Website';
    
    const pageDescription = metaSettings.siteDescription || 
                           `Visit ${metaSettings.siteName || originalHost} for amazing content and services.`;
    
    const ogImageUrl = metaSettings.ogImage || `${window.location.origin}/og-image.jpg`;
    const faviconUrl = metaSettings.customFavicon || `${window.location.origin}/favicon.ico`;

    // Set document title
    document.title = pageTitle;

    // Helper function to set or update meta tag
    const setMetaTag = (selector, content, property = 'content') => {
      let meta = document.querySelector(selector);
      if (!meta) {
        meta = document.createElement('meta');
        if (selector.includes('property=')) {
          meta.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        } else if (selector.includes('name=')) {
          meta.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute(property, content);
    };

    // Helper function to set or update link tag
    const setLinkTag = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Set basic meta tags
    setMetaTag('meta[name="description"]', pageDescription);
    
    // Set favicon
    setLinkTag('icon', faviconUrl);
    setLinkTag('shortcut icon', faviconUrl);
    
    // Set Open Graph meta tags
    setMetaTag('meta[property="og:title"]', pageTitle);
    setMetaTag('meta[property="og:description"]', pageDescription);
    setMetaTag('meta[property="og:type"]', 'website');
    setMetaTag('meta[property="og:url"]', window.location.href);
    setMetaTag('meta[property="og:site_name"]', metaSettings.siteName || originalHost);
    setMetaTag('meta[property="og:image"]', ogImageUrl);
    setMetaTag('meta[property="og:image:width"]', '1200');
    setMetaTag('meta[property="og:image:height"]', '630');
    
    // Set Twitter Card meta tags
    setMetaTag('meta[name="twitter:card"]', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', pageTitle);
    setMetaTag('meta[name="twitter:description"]', pageDescription);
    setMetaTag('meta[name="twitter:image"]', ogImageUrl);
    
    // Set additional SEO meta tags
    setMetaTag('meta[name="robots"]', 'index, follow');
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.href);

    console.log('🏷️ PagePublic: Custom meta tags applied:', {
      title: pageTitle,
      description: pageDescription,
      favicon: faviconUrl,
      ogImage: ogImageUrl
    });
  };

  // Update meta tags when pageData changes
  useEffect(() => {
    if (customMetaSettings && pageData) {
      applyCustomMetaTags(customMetaSettings);
    }
  }, [pageData, customMetaSettings]);

  // 🐛 DEBUG: Navigation Status
  useEffect(() => {
    if (pageData) {
      console.log('🧭 PagePublic Navigation Debug:', {
        hasNavigation: !!pageData.navigation,
        navigationEnabled: pageData.navigation?.enabled,
        navigationData: pageData.navigation,
        pageSource: pageSource
      });
      
      // 🐛 DEBUG: HeroHeader Container Sections Check
      const heroHeaders = pageData.widgets?.filter(w => w.type === 'HeroHeader') || [];
      if (heroHeaders.length > 0) {
        heroHeaders.forEach((hero, index) => {
          console.log(`🎨 HeroHeader #${index + 1} Debug:`, {
            hasContainerSections: !!hero.containerSections,
            containerCount: hero.containerSections?.length || 0,
            firstContainerMargins: hero.containerSections?.[0] ? {
              marginTop: hero.containerSections[0].containerMarginTop,
              marginRight: hero.containerSections[0].containerMarginRight,
              marginBottom: hero.containerSections[0].containerMarginBottom,
              marginLeft: hero.containerSections[0].containerMarginLeft,
              marginTopMobile: hero.containerSections[0].containerMarginTopMobile,
            } : null,
            firstContainerPaddings: hero.containerSections?.[0] ? {
              paddingTop: hero.containerSections[0].containerPaddingTop,
              paddingRight: hero.containerSections[0].containerPaddingRight,
              paddingBottom: hero.containerSections[0].containerPaddingBottom,
              paddingLeft: hero.containerSections[0].containerPaddingLeft,
            } : null,
            hasContentElements: hero.containerSections?.[0]?.contentElements?.length > 0,
            contentElementsDetails: hero.containerSections?.[0]?.contentElements?.map((el, i) => ({
              index: i,
              elementType: el.elementType,
              hasContent: !!el.content,
              margins: {
                top: el.marginTop,
                right: el.marginRight,
                bottom: el.marginBottom,
                left: el.marginLeft,
                topMobile: el.marginTopMobile,
                bottomMobile: el.marginBottomMobile
              },
              fontSize: el.fontSize,
              fontSizeMobile: el.fontSizeMobile
            })) || []
          });
        });
      }
    }
  }, [pageData, pageSource]);

  // Load custom meta settings for custom domains
  const loadCustomMetaSettings = async (userId, spaceId) => {
    setMetaLoading(true);
    
    try {
      // Try to load from public spacenames first (accessible without auth)
      const spacenamesRef = ref(db, `/spacenames/${spaceId}`);
      const spacenamesSnapshot = await get(spacenamesRef);
      
      if (spacenamesSnapshot.exists()) {
        const spaceInfo = spacenamesSnapshot.val();
        
        // Try to load meta settings from public space data
        const publicSpaceRef = ref(db, `/publicSpaces/${spaceId}`);
        const publicSpaceSnapshot = await get(publicSpaceRef);
        
        if (publicSpaceSnapshot.exists()) {
          const publicSpaceData = publicSpaceSnapshot.val();
          const metaSettings = {
            siteTitle: publicSpaceData.siteTitle || '',
            siteDescription: publicSpaceData.siteDescription || '',
            customFavicon: publicSpaceData.customFavicon || '',
            ogImage: publicSpaceData.ogImage || '',
            siteName: publicSpaceData.siteName || spaceInfo.spaceName || ''
          };
          
          console.log('🏷️ PagePublic: Custom meta settings loaded from public space:', metaSettings);
          
          // Apply meta tags BEFORE setting state to prevent flicker
          applyCustomMetaTags(metaSettings);
          
          setCustomMetaSettings(metaSettings);
          setMetaLoading(false);
          
          return metaSettings;
        }
        
        // Fallback: Use basic space info
        const fallbackSettings = {
          siteTitle: '',
          siteDescription: '',
          customFavicon: '',
          ogImage: '',
          siteName: spaceInfo.spaceName || ''
        };
        
        console.log('🏷️ PagePublic: Using fallback meta settings:', fallbackSettings);
        
        // Apply fallback meta tags
        applyCustomMetaTags(fallbackSettings);
        
        setCustomMetaSettings(fallbackSettings);
        setMetaLoading(false);
        
        return fallbackSettings;
      }
    } catch (error) {
      console.error('❌ Error loading custom meta settings:', error);
    }
    
    setMetaLoading(false);
    return null;
  };

  // Helper function for new space system routing under /to/ prefix
  const handleSpaceSystemRoute = async (username, pagename) => {
    console.log("🔍 PagePublic: Looking up spacename:", username);
    const spaceInfo = await getSpaceBySpacename(username);
    console.log("🔍 PagePublic: getSpaceBySpacename result:", spaceInfo);
    
    if (!spaceInfo) {
      console.log("❌ Spacename not found:", username);
      return;
    }
    
    // 🔥 FIX: Cache spaceId in window object for CustomPageLoading
    if (spaceInfo.spaceId) {
      window.__BC_RESOLVED_SPACE_ID__ = spaceInfo.spaceId;
      console.log("💾 PagePublic: Cached spaceId for loading screen:", spaceInfo.spaceId);
    }
    
    // Load custom meta settings for custom domains
    const originalHost = window.__BC_ORIGINAL_HOST__ || 
                        new URLSearchParams(window.location.search).get('__bc_original_host');
    if (originalHost) {
      console.log("🏷️ PagePublic: Loading custom meta settings for custom domain:", originalHost);
      await loadCustomMetaSettings(spaceInfo.userId, spaceInfo.spaceId);
    }
    
    // Determine page to load (home if no pagename, otherwise the specific page)
    const targetPageName = pagename || 'home';
    console.log("🎯 PagePublic: Target page:", targetPageName);
    
    // Try loading from Firestore first (if enabled)
    const firestorePageData = await loadPageFromFirestore(username, pagename, async () => {
      console.log("🔄 PagePublic: Firestore fallback to RTDB for:", { username, pagename });
      return null;
    });
    
    if (firestorePageData) {
      console.log("✅ PagePublic: Page loaded from Firestore:", firestorePageData);
      // 🔥 CRITICAL FIX: Set currentPageId for NavBarIntegration
      setCurrentPageId(`${spaceInfo.userId}/${spaceInfo.spaceId}/${targetPageName}`);
      setPageData(firestorePageData.pageData);
      setPageSource('firestore');
      setVersionInfo(firestorePageData.versionInfo);
      
      // 🔥 FIX: Load custom fonts BEFORE ending loading state
      console.log('🔤 PagePublic (Custom Domain): Loading custom fonts...');
      try {
        // Load page custom fonts
        const pageFonts = await loadPageCustomFonts(firestorePageData.pageData);
        let userFonts = [];
        
        // Load user custom fonts from RTDB public space
        if (firestorePageData.pageData.userId && firestorePageData.pageData.spaceId) {
          userFonts = await loadUserCustomFonts(firestorePageData.pageData.userId, firestorePageData.pageData.spaceId);
        }
        
        // ✅ CRITICAL FIX: Also load userCustomFonts if they exist in the page data (from Firestore)
        if (firestorePageData.pageData.userCustomFonts && Array.isArray(firestorePageData.pageData.userCustomFonts)) {
          console.log(`📱 Loading ${firestorePageData.pageData.userCustomFonts.length} user custom fonts from Firestore page data`);
          for (const font of firestorePageData.pageData.userCustomFonts) {
            await injectCustomFont(font);
          }
          userFonts = [...userFonts, ...firestorePageData.pageData.userCustomFonts];
        }
        
        console.log('✅ PagePublic (Custom Domain): All fonts loaded successfully');
      } catch (err) {
        console.warn('⚠️ PagePublic (Custom Domain): Font loading failed, continuing with fallback fonts:', err);
      }
      
      setLoading(false); // 🔥 FIX: Set loading to false AFTER fonts are loaded
      return;
    }
    
    // Fallback to RTDB
    const pageRef = ref(db, `/pages/${spaceInfo.userId}/${spaceInfo.spaceId}/${targetPageName}`);
    const pageSnapshot = await get(pageRef);
    
    if (pageSnapshot.exists()) {
      const loadedPageData = pageSnapshot.val();
      console.log("✅ PagePublic: Page loaded from RTDB:", loadedPageData);
      setCurrentPageId(`${spaceInfo.userId}/${spaceInfo.spaceId}/${targetPageName}`);
      setPageData(loadedPageData);
      setPageSource('rtdb');
      
      // Load custom fonts
      await loadPageCustomFonts(loadedPageData);
      if (loadedPageData.userId) {
        await loadUserCustomFonts(loadedPageData.userId, loadedPageData.spaceId || spaceInfo?.spaceId);
      }
    } else {
      console.log("❌ Page not found:", targetPageName);
    }
  };

  // Helper function for legacy routing
  const handleLegacyRoute = async (customRoute) => {
    console.log("🔍 PagePublic: Loading legacy page:", customRoute);
    
    // Try loading from live pages first
    const livePageRef = ref(db, `/livePages/${customRoute}`);
    const livePageSnapshot = await get(livePageRef);
    
    if (livePageSnapshot.exists()) {
      const livePageData = livePageSnapshot.val();
      console.log("✅ PagePublic: Legacy page loaded from livePages:", livePageData);
      setCurrentPageId(customRoute);
      setPageData(livePageData);
      setPageSource('live');
      
      // Load custom fonts
      await loadPageCustomFonts(livePageData);
      if (livePageData.userId) {
        await loadUserCustomFonts(livePageData.userId, livePageData.spaceId);
      }
    } else {
      console.log("❌ Legacy page not found in livePages:", customRoute);
    }
  };

  const auth = getAuth();

  useEffect(() => {
    const loadPage = async () => {
      try {
        // 🔥 FIX: Clear cached spaceId when loading a new page
        delete window.__BC_RESOLVED_SPACE_ID__;
        
        console.log('📄 PagePublic.js loadPage started:', {
          hostname: window.location.hostname,
          pathname: window.location.pathname,
          params: { customRoute, username, pagename },
          isCustomDomain: isCustomDomain()
        });
        
        // Prüfe ob Custom Domain oder BonusClub Subdomain
        if (isCustomDomain()) {
          // 🔥 FIX: Check if this is a proxy request
          const originalHost = window.__BC_ORIGINAL_HOST__ || 
                              new URLSearchParams(window.location.search).get('__bc_original_host');
          
          // If we have originalHost, this is a proxy request
          if (originalHost) {
            console.log("🔄 PagePublic: Proxy request detected, using KV mapping data", {
              originalHost,
              currentPath: window.location.pathname,
              currentHostname: window.location.hostname
            });
            
            // For proxy requests, we know the mapping from the Worker's KV storage
            // The Worker constructs URLs like: /to/{username} or /to/{username}/{pagename}
            // Extract username from current URL (NEVER use hardcoded defaults!)
            
            let targetUsername = null;
            let targetPagename = null;
            
            // 🔥 FIX: Für Custom Domains IMMER aus Firebase laden, nie aus URL!
            // Der Worker KV Cache kann veraltet sein (bis zu 60s)
            console.log("🔄 Custom Domain: Loading mapping directly from Firebase instead of URL");
            
            try {
              // 🔥 FIX: Use getDomainMapping helper (loads from /cloudflare/mappings/ first!)
              const { getDomainMapping } = await import('../utils/domainHelpers');
              const mapping = await getDomainMapping(originalHost);
              
              if (mapping) {
                // 🔥 FIX: Try username first, fallback to spaceId
                targetUsername = mapping.username || mapping.spaceId;
                console.log("✅ Loaded mapping from Firebase RTDB:", { 
                  domain: originalHost, 
                  username: targetUsername,
                  source: mapping.source,
                  fullMapping: mapping 
                });
                
                // Extract pagename from current path if not home
                const currentPath = window.location.pathname;
                if (currentPath && currentPath !== '/' && !currentPath.includes('/to/')) {
                  targetPagename = currentPath.replace(/^\//, '');
                }
              } else {
                console.warn("⚠️ No Firebase mapping found, falling back to URL extraction");
                
                // Fallback: Extract from URL if it contains /to/
                if (window.location.href.includes('/to/')) {
                  const urlMatch = window.location.href.match(/\/to\/([^\/\?]+)(?:\/([^\/\?]+))?/);
                  if (urlMatch) {
                    targetUsername = urlMatch[1];
                    targetPagename = urlMatch[2] || null;
                    console.log("✅ Extracted from /to/ URL (fallback):", { targetUsername, targetPagename });
                  }
                }
              }
            } catch (error) {
              console.error("❌ Error loading Firebase mapping:", error);
              
              // Fallback: Extract from URL
              if (window.location.href.includes('/to/')) {
                const urlMatch = window.location.href.match(/\/to\/([^\/\?]+)(?:\/([^\/\?]+))?/);
                if (urlMatch) {
                  targetUsername = urlMatch[1];
                  targetPagename = urlMatch[2] || null;
                  console.log("✅ Extracted from /to/ URL (error fallback):", { targetUsername, targetPagename });
                }
              }
            }
            
            // If we couldn't extract username, this is an error
            if (!targetUsername) {
              console.error("❌ PagePublic: Could not extract username from proxy URL", {
                originalHost,
                currentUrl: window.location.href,
                pathname: window.location.pathname
              });
              
              // Try to resolve dynamically from originalHost
              console.log("🔄 Attempting to resolve domain from Firebase:", originalHost);
              
              try {
                // Try to load from Firestore first (new system)
                const firestoreResult = await loadCustomDomainPageFromFirestore(originalHost, window.location.pathname);
                
                if (firestoreResult) {
                  console.log("✅ Resolved from Firestore:", firestoreResult);
                  setPageData(firestoreResult.pageData);
                  setPageSource(firestoreResult.source);
                  setVersionInfo({ version: firestoreResult.version });
                  
                  // Load custom fonts
                  await loadPageCustomFonts(firestoreResult.pageData);
                  if (firestoreResult.pageData.userId) {
                    await loadUserCustomFonts(firestoreResult.pageData.userId, firestoreResult.pageData.spaceId);
                  }
                  
                  setLoading(false);
                  return;
                }
                
                // Fallback to RTDB domain resolution
                const rtdbResult = await resolvePageForDomain(originalHost, window.location.pathname);
                
                if (rtdbResult) {
                  console.log("✅ Resolved from RTDB:", rtdbResult);
                  const { pageData: resolvedPageData, pageId } = rtdbResult;
                  
                  setCurrentPageId(pageId);
                  setPageData(resolvedPageData);
                  
                  // Load custom fonts
                  await loadPageCustomFonts(resolvedPageData);
                  if (resolvedPageData.userId) {
                    await loadUserCustomFonts(resolvedPageData.userId, resolvedPageData.spaceId);
                  }
                  
                  setLoading(false);
                  return;
                }
                
                // If we still couldn't resolve, show error
                console.error("❌ Could not resolve domain:", originalHost);
                setPageData(null);
                setLoading(false);
                return;
                
              } catch (error) {
                console.error("❌ Error resolving domain:", error);
                setPageData(null);
                setLoading(false);
                return;
              }
            }
            
            // For direct custom domain access without /to/, use the current path as pagename
            if (!window.location.href.includes('/to/')) {
              const currentPath = window.location.pathname;
              if (currentPath && currentPath !== '/') {
                targetPagename = currentPath.replace(/^\//, ''); // Remove leading slash
              }
            }
            
            console.log("🎯 PagePublic: Using target:", { targetUsername, targetPagename, currentPath: window.location.pathname });
            
            // Use space system directly with subpage support
            await handleSpaceSystemRoute(targetUsername, targetPagename);
            setLoading(false);
            return;
          }
          
          // Standard custom domain resolution
          const domain = originalHost || window.location.hostname;
          const path = window.location.pathname;
          
          console.log("🌐 PagePublic: Custom Domain detected:", domain, "Path:", path, {
            originalHost,
            currentHostname: window.location.hostname,
            isProxied: !!originalHost
          });
          
          const result = await resolvePageForDomain(domain, path);
          
          if (result) {
            const { pageData: resolvedPageData, pageId, domainMapping: mapping } = result;
            
            console.log("✅ PagePublic: Custom Domain resolved:", { pageId, pageData: resolvedPageData });
            
            setCurrentPageId(pageId);
            setDomainMapping(mapping);
            
            // Draft mode is now handled by Live/Draft toggle - no separate check needed
            
            // Custom Domain pages are loaded from livePages, so they're always "active"
            // (resolvePageForDomain already handles the live/staging logic)
            
            // Update View Counter (in staging for analytics)
            const viewCountRef = ref(db, `/pages/${pageId}/viewCounter`);
            const viewSnapshot = await get(viewCountRef);
            const currentCount = viewSnapshot.val() || 0;
            await set(viewCountRef, currentCount + 1);
            
            // Also update view counter in live pages
            const liveViewCountRef = ref(db, `/livePages/${pageId}/viewCounter`);
            await set(liveViewCountRef, (resolvedPageData.viewCounter || 0) + 1);
            
            setPageData(resolvedPageData);
            
            // Load custom fonts for this page
            await loadPageCustomFonts(resolvedPageData);
            if (resolvedPageData.userId) {
              await loadUserCustomFonts(resolvedPageData.userId, resolvedPageData.spaceId);
            }
          } else {
            console.log("❌ No page found for custom domain:", domain, path);
          }
          
        } else if (customRoute) {
          // Check if this is the new /to/username/pagename format or legacy /to/customRoute
          console.log("🔗 PagePublic: /to/ Route detected:", { customRoute, username, pagename });
          
          // If we have both customRoute and username, it's the new format: /to/username/pagename
          if (username) {
            console.log("🏠 PagePublic: New /to/username/pagename format detected");
            // Handle new space system under /to/ prefix
            await handleSpaceSystemRoute(username, pagename);
          } else {
            console.log("🏠 PagePublic: Legacy /to/customRoute format detected");
            // Handle legacy system: /to/customRoute
            await handleLegacyRoute(customRoute);
          }
        } else if (username) {
          // Direct Space System Routing: bonusclub.link/username/pagename (fallback for old URLs)
          console.log("🏠 PagePublic: Direct Space System Route detected:", { username, pagename });
          console.log("⚠️ PagePublic: This is deprecated, should use /to/ prefix");
          
          // Load from new structure: Try both 2-level and 3-level
          console.log("🔍 PagePublic: Looking up spacename:", username);
          const spaceInfo = await getSpaceBySpacename(username);
          console.log("🔍 PagePublic: getSpaceBySpacename result:", spaceInfo);
          
          if (!spaceInfo) {
            console.log("❌ Spacename not found:", username);
            
            // DEBUG: Let's check what's actually in the database
            const db = getDatabase();
            const spacenameRef = ref(db, `/spacenames/`);
            const allSpacenamesSnapshot = await get(spacenameRef);
            console.log("🔍 DEBUG: All spacenames in DB:", allSpacenamesSnapshot.val());
            
            // MANUAL FIX: Create missing spacename mapping for existing space
            // Only attempt this if user is authenticated to avoid permission errors
            const auth = getAuth();
            const currentUser = auth.currentUser;
            
            if (currentUser && (!allSpacenamesSnapshot.val() || !allSpacenamesSnapshot.val()[username])) {
              console.log("🔧 MANUAL FIX: Creating missing spacename mapping for:", username);
              
              try {
                // Try to find user by looking at existing spaces
                const usersRef = ref(db, `/users/`);
                const allUsersSnapshot = await get(usersRef);
                const allUsers = allUsersSnapshot.val();
                
                if (allUsers) {
                  for (const userId of Object.keys(allUsers)) {
                    const userSpaces = allUsers[userId]?.spaces;
                    if (userSpaces && userSpaces[username]) {
                      console.log("🔧 Found space, creating spacename mapping:", { userId, username });
                      
                      const spacenameMapping = {
                        userId,
                        spaceId: username,
                        spaceName: userSpaces[username].spaceName,
                        createdAt: new Date().toISOString()
                      };
                      
                      const newSpacenameRef = ref(db, `/spacenames/${username}`);
                      await set(newSpacenameRef, spacenameMapping);
                      
                      console.log("✅ Spacename mapping created, reloading page...");
                      window.location.reload();
                      return;
                    }
                  }
                }
              } catch (error) {
                console.error("❌ Error creating spacename mapping:", error);
                // Continue loading without the mapping
              }
            } else if (!currentUser && (!allSpacenamesSnapshot.val() || !allSpacenamesSnapshot.val()[username])) {
              console.log("⚠️ Spacename mapping missing but user not authenticated. Skipping auto-fix.");
            }
            
            // Show a better error message for missing spacenames
            console.log(`❌ Space "${username}" not found. Available spaces:`, Object.keys(allSpacenamesSnapshot.val() || {}));
            setLoading(false);
            return;
          }
          
          // Determine page to load
          let pageKey;
          if (!pagename) {
            // Root path: /username/ → Load home page
            pageKey = 'home';
          } else {
            // Sub path: /username/about → Load specific page
            pageKey = pagename;
          }
          
          console.log("🔍 PagePublic: Looking for page", { pagename, pageKey, isRoot: !pagename });
          
          // 🚀 NEW: Try Firestore first with intelligent fallback
          const rtdbFallback = async () => {
            console.log("🔄 PagePublic: Using RTDB fallback for space system");
            
            // Try 3-level structure first (userId/spaceId/pageName) - READ FROM LIVE PAGES
            const userPagesRef = ref(db, `/livePages/${spaceInfo.userId}`);
            const userPagesSnapshot = await get(userPagesRef);
            
            return { userPagesSnapshot, userPagesRef };
          };
          
          // ✅ PERFORMANCE: Load from Firestore and set loading state immediately
          const firestoreResult = await loadPageFromFirestore(username, pagename, rtdbFallback);
          
          if (firestoreResult && firestoreResult.pageData && firestoreResult.source?.includes('firestore')) {
            console.log("✅ PagePublic: Page loaded from Firestore:", firestoreResult);
            
            // ✅ PERFORMANCE: Set page data immediately to start rendering
            setCurrentPageId(firestoreResult.pageData.userId + '/' + spaceInfo.spaceId + '/' + pageKey);
            setPageData(firestoreResult.pageData);
            setPageSource(firestoreResult.source);
            setVersionInfo({
              version: firestoreResult.version,
              siteInfo: firestoreResult.siteInfo
            });
            
            console.log('🔍 PagePublic: versionInfo set:', {
              version: firestoreResult.version,
              siteInfo: firestoreResult.siteInfo ? 'present' : 'missing',
              siteInfoKeys: firestoreResult.siteInfo ? Object.keys(firestoreResult.siteInfo) : []
            });
            
            // ✅ CRITICAL: Load fonts BEFORE rendering to prevent FOUC (Flash of Unstyled Content)
            console.log('🔤 PagePublic: Loading custom fonts BEFORE render to prevent flickering...');
            try {
              // Load page custom fonts
              const pageFonts = await loadPageCustomFonts(firestoreResult.pageData);
              let userFonts = [];
              
              // Load user custom fonts from RTDB public space
              if (firestoreResult.pageData.userId && firestoreResult.pageData.spaceId) {
                userFonts = await loadUserCustomFonts(firestoreResult.pageData.userId, firestoreResult.pageData.spaceId);
              }
              
              // ✅ CRITICAL FIX: Also load userCustomFonts if they exist in the page data (from Firestore)
              if (firestoreResult.pageData.userCustomFonts && Array.isArray(firestoreResult.pageData.userCustomFonts)) {
                console.log(`📱 Loading ${firestoreResult.pageData.userCustomFonts.length} user custom fonts from Firestore page data`);
                for (const font of firestoreResult.pageData.userCustomFonts) {
                  await injectCustomFont(font);
                }
                userFonts = [...userFonts, ...firestoreResult.pageData.userCustomFonts];
              }
              
              console.log('✅ PagePublic: All fonts loaded successfully - preventing FOUC');
            } catch (err) {
              console.warn('⚠️ PagePublic: Font loading failed, continuing with fallback fonts:', err);
            }
            
            // ✅ Stop loading AFTER fonts are loaded to prevent flickering
            setLoading(false);
            
            // ✅ RICH TEXT FIXER DEAKTIVIERT - Verwende nur HeroHeader dangerouslySetInnerHTML Fix
            console.log('🔧 PagePublic: Rich Text Fixer deaktiviert - verwende HeroHeader-Fix');
            
            
            return;
          }
          
          // Continue with RTDB logic if Firestore didn't work
          console.log("⚠️ PagePublic: Firestore not available, continuing with RTDB...");
          const { userPagesSnapshot } = await rtdbFallback();
          
          if (userPagesSnapshot.exists()) {
            const userPagesData = userPagesSnapshot.val();
            let foundPage = null;
            let foundPagePath = null;
            
            // Search ONLY in the specific space (not all spaces!)
            const targetSpaceData = userPagesData[spaceInfo.spaceId];
            if (targetSpaceData && targetSpaceData[pageKey]) {
              foundPage = targetSpaceData[pageKey];
              foundPagePath = `${spaceInfo.userId}/${spaceInfo.spaceId}/${pageKey}`;
              console.log("✅ Found page in specific space:", { 
                spaceId: spaceInfo.spaceId, 
                pageKey, 
                isHomePage: foundPage.isHomePage,
                username: spaceInfo.username || username
              });
            } else {
              console.log("❌ Page not found in specific space:", { 
                spaceId: spaceInfo.spaceId, 
                pageKey,
                availablePages: targetSpaceData ? Object.keys(targetSpaceData) : 'space not found'
              });
              
            }
            
            // If not found in spaces, try 2-level structure (also from livePages)
            if (!foundPage && userPagesData[pageKey]?.widgets) {
              foundPage = userPagesData[pageKey];
              foundPagePath = `${spaceInfo.userId}/${pageKey}`;
            }
            
            if (foundPage) {
              console.log("✅ Space page loaded:", { username, pageKey, foundPagePath });
              
              // Draft mode is now handled by Live/Draft toggle - no separate check needed
              
              setCurrentPageId(foundPagePath);
              
              // Update View Counter (still update in staging for analytics)
              const viewCountRef = ref(db, `/pages/${foundPagePath}/viewCounter`);
              const viewSnapshot = await get(viewCountRef);
              const currentCount = viewSnapshot.val() || 0;
              await set(viewCountRef, currentCount + 1);
              
              // Also update view counter in live pages for consistency
              const liveViewCountRef = ref(db, `/livePages/${foundPagePath}/viewCounter`);
              await set(liveViewCountRef, currentCount + 1);
              
              setPageData(foundPage);
              
              // Load custom fonts for this page with mobile optimization
              const pageFonts = await loadPageCustomFonts(foundPage);
              let userFonts = [];
              
              // 🔧 CRITICAL FIX: Load fonts from multiple sources
              if (foundPage.userId) {
                userFonts = await loadUserCustomFonts(foundPage.userId, foundPage.spaceId);
              }
              
              // Also load userCustomFonts if they exist in the page data (from Firestore)
              if (foundPage.userCustomFonts && Array.isArray(foundPage.userCustomFonts)) {
                console.log(`📱 Loading ${foundPage.userCustomFonts.length} user custom fonts from page data`);
                for (const font of foundPage.userCustomFonts) {
                  await injectCustomFont(font);
                }
                userFonts = [...userFonts, ...foundPage.userCustomFonts];
              }
              
              // Preload fonts for mobile optimization
              const allFonts = [...pageFonts, ...userFonts];
              preloadFontsForMobile(allFonts);
              
              setLoading(false);
              
              // ✅ RICH TEXT FIXER: Initialisiere nach dem Laden der Seite
              setTimeout(() => {
                initLivePageRichTextFixer();
                startLivePageRichTextWatcher();
              }, 500);
              
            } else {
              console.log("❌ No page found in space:", { username, pageKey });
              setLoading(false);
            }
          } else {
            console.log("❌ No user pages found:", username);
            setLoading(false);
          }
          
        } else if (customRoute) {
          // Legacy BonusClub Routing: bonusclub.link/to/customRoute
          const normalizedRoute = customRoute.toLowerCase();
          console.log("🔗 Legacy BonusClub Route:", normalizedRoute);

          const publicPageRef = ref(db, `/publicPages/${normalizedRoute}`);
          const snapshot = await get(publicPageRef);
          
          if (snapshot.exists()) {
            const { pageId, userId } = snapshot.val();
            console.log("✅ Legacy route resolved:", { pageId, userId });
            
            setCurrentPageId(pageId);

            // Check if page exists in live pages (new system)
            const livePageRef = ref(db, `/livePages/${pageId}`);
            const livePageSnapshot = await get(livePageRef);
            
            if (livePageSnapshot.exists()) {
              // Page exists in live pages - load from there
              const livePageData = livePageSnapshot.val();
              console.log("📄 Legacy page data loaded from live:", livePageData);
              
              // Update View Counter (in staging for analytics)
              const viewCountRef = ref(db, `/pages/${pageId}/viewCounter`);
              const viewSnapshot = await get(viewCountRef);
              const currentCount = viewSnapshot.val() || 0;
              await set(viewCountRef, currentCount + 1);
              
              // Also update view counter in live pages
              const liveViewCountRef = ref(db, `/livePages/${pageId}/viewCounter`);
              await set(liveViewCountRef, (livePageData.viewCounter || 0) + 1);
              
              setPageData(livePageData);
              
              // Load custom fonts for this page
              await loadPageCustomFonts(livePageData);
              if (livePageData.userId) {
                await loadUserCustomFonts(livePageData.userId, livePageData.spaceId);
              }
              
              setLoading(false);
            } else {
              // Fallback: Check old isActive system for backward compatibility
              const isActiveRef = ref(db, `/pages/${pageId}/isActive`);
              const isActiveSnapshot = await get(isActiveRef);
              const isActive = isActiveSnapshot.val();

              if (isActive) {
                console.log("⚠️ Legacy page using old isActive system - consider publishing to live");
                
                // Update View Counter
                const viewCountRef = ref(db, `/pages/${pageId}/viewCounter`);
                const viewSnapshot = await get(viewCountRef);
                const currentCount = viewSnapshot.val() || 0;
                await set(viewCountRef, currentCount + 1);

                // Load from staging (old system)
                const pageRef = ref(db, `/pages/${pageId}`);
                onValue(pageRef, async (pageSnapshot) => {
                  const data = pageSnapshot.val();
                  console.log("📄 Legacy page data loaded from staging (fallback):", data);
                  setPageData(data);
                  
                  // Load custom fonts for this page
                  await loadPageCustomFonts(data);
                  if (data.userId) {
                    await loadUserCustomFonts(data.userId, data.spaceId);
                  }
                  
                  setLoading(false);
                });
              } else {
                console.log("❌ Legacy page is not published or active");
                setLoading(false);
              }
            }
          } else {
            console.log("❌ No page found for legacy route:", normalizedRoute);
            setLoading(false);
          }
        } else {
          console.log("❌ No valid route parameters found");
          setLoading(false);
        }
        
      } catch (error) {
        console.error("❌ Error loading page:", error);
        setLoading(false);
      }
    };

    loadPage();

    // Load products for popup functionality
    const loadProducts = async () => {
      try {
        // Get userId from current page context
        let pageOwnerId = null;
        
        if (isCustomDomain() && domainMapping) {
          // For custom domains, get userId from domain mapping
          pageOwnerId = domainMapping.userId;
        } else if (customRoute) {
          // For BonusClub routes, get userId from publicPages
          const publicPageRef = ref(db, `/publicPages/${customRoute.toLowerCase()}`);
          const pageSnapshot = await get(publicPageRef);
          if (pageSnapshot.exists()) {
            const pageInfo = pageSnapshot.val();
            pageOwnerId = pageInfo.userId;
          }
        }
          
        console.log('🔍 PagePublic: Loading products for user:', pageOwnerId);
        
        if (pageOwnerId) {
          try {
            // Try to load user's products
            const userProductsRef = ref(db, `users/${pageOwnerId}/products`);
            const productsSnapshot = await get(userProductsRef);
            
            if (productsSnapshot.exists()) {
              const productsData = productsSnapshot.val();
              const productsArray = Object.keys(productsData).map(key => ({
                id: key,
                ...productsData[key]
              }));
              
              console.log('✅ PagePublic: Products loaded:', productsArray.length, 'products');
              console.log('✅ PagePublic: Product IDs:', productsArray.map(p => p.id));
              setProducts(productsArray);
            } else {
              console.log('⚠️ PagePublic: No products found for user');
              setProducts([]);
            }
          } catch (productError) {
            console.warn('⚠️ PagePublic: Cannot load products (permission denied):', productError.message);
            console.log('🔄 PagePublic: Continuing without products - popups will use passed product data');
            setProducts([]);
          }
        }
      } catch (error) {
        console.error('❌ PagePublic: Error loading products:', error);
        setProducts([]);
      }
    };

    loadProducts();

  }, [db, customRoute]);

  // Check access control after page is loaded
  useEffect(() => {
    if (pageData && !loading) {
      checkPageAccess();
    }
  }, [pageData, loading]);

  const checkPageAccess = () => {
    console.log('🔒 PagePublic: Checking page access with data:', {
      isOnlyForMembers: pageData.isOnlyForMembers,
      productId: pageData.accessControlProductId,
      productName: pageData.accessControlProductName,
      language: pageData.accessControlLanguage
    });
    
    // If page is not restricted, grant access immediately
    if (!pageData.isOnlyForMembers || !pageData.accessControlProductId) {
      setAccessGranted(true);
      setShowAccessVerification(false);
      return;
    }

    // If page is restricted, show verification
    setAccessGranted(false);
    setShowAccessVerification(true);
  };

  const handleAccessGranted = () => {
    setAccessGranted(true);
    setShowAccessVerification(false);
  };

  const handleAccessDenied = () => {
    setAccessGranted(false);
    setShowAccessVerification(true);
  };

  const openWhatsApp = (number) => {
    window.open(`https://wa.me/${number}`, '_blank');
  };


  // Check if we need to wait for meta loading (for custom domains)
  const originalHost = window.__BC_ORIGINAL_HOST__ || 
                      new URLSearchParams(window.location.search).get('__bc_original_host');
  const shouldWaitForMeta = originalHost && metaLoading;

  if (loading || shouldWaitForMeta) {
    // NEW: Custom Loading Screen (NO BonusClub branding)
    // Extract spaceId from URL path if available (no hardcoded mappings!)
    const originalHost = window.__BC_ORIGINAL_HOST__ || 
                        new URLSearchParams(window.location.search).get('__bc_original_host');
    
    let customDomainSpaceId = null;
    
    // Try to extract from /to/ route
    if (window.location.pathname.includes('/to/')) {
      const pathMatch = window.location.pathname.match(/\/to\/([^\/\?]+)/);
      if (pathMatch) {
        customDomainSpaceId = pathMatch[1];
      }
    }
    
    // If we have pageData, extract spaceId from it
    if (!customDomainSpaceId && pageData?.spaceId) {
      customDomainSpaceId = pageData.spaceId;
    }
    
    return <CustomPageLoading 
      pageData={pageData} 
      spaceInfo={versionInfo?.siteInfo} 
      source={pageSource}
      customDomainSpaceId={customDomainSpaceId}
    />;
  }

  if (!pageData || !pageData.widgets) {
    return <div>
      <EmptyPage />
    </div>;
  }

  // Show access verification if page is restricted and access not granted
  if (showAccessVerification && !accessGranted) {
    return (
      <AccessVerification
        pageData={pageData}
        onAccessGranted={handleAccessGranted}
        onAccessDenied={handleAccessDenied}
      />
    );
  }

  // If page is restricted but access not granted, don't show content
  if (pageData.isOnlyForMembers && !accessGranted) {
    return <CustomPageLoading 
      pageData={pageData} 
      spaceInfo={versionInfo?.siteInfo} 
      source={pageSource}
    />;
  }

  const renderWidget = (widget) => {
    // ✅ KRITISCH: Vollständige Props für Container-basierte Mobile-Detection
    const commonProps = {
      data: widget,
      isEditMode: false,
      isSelected: false,
      onRemove: () => {}, // Dummy function für Live-Mode
      onUpdate: () => {},  // Dummy function für Live-Mode
      onSelectWidget: () => {} // Dummy function für Live-Mode
    };

    switch (widget.type) {
        case 'HeroHeader':
          return <HeroHeader {...commonProps} />;
        case 'HiWidget':
          return <HiWidget {...commonProps} />;
        case 'VideoTeaser':
          return <VideoTeaser {...commonProps} />;
        case 'SimplisticHeader':
          return <SimplisticHeader {...commonProps} />;
        case 'SupremeTestimonial':
          return <SupremeTestimonial {...commonProps} key={widget.id} />;
        case 'SimplisticQuote':
          return <SimplisticQuote {...commonProps} key={widget.id} />;
        case 'MethodologyWidget':
          return <MethodologyWidget {...commonProps} key={widget.id} />;
        case 'PersonalBrandingWidget':
          return <PersonalBrandingWidget {...commonProps} key={widget.id} />;
        case 'SimplisticTestimonial':
          return <SimplisticTestimonial {...commonProps} key={widget.id} />;
        case 'SimplisticServices':
          return <SimplisticServices {...commonProps} key={widget.id} />;
        case 'BulletPointsWidget':
          return <BulletPointsWidget {...commonProps} key={widget.id} />;
        case 'ComparisonWidget':
          return <ComparisonWidget {...commonProps} key={widget.id} />;
        case 'ProductInfoWidget':
          return <ProductInfoWidget key={`${widget.id}-${widget.productId}`} data={widget} isEditMode={false} />;
        case 'EventInfoWidget':
          return <EventInfoWidget key={`${widget.id}-${widget.eventId}`} data={widget} isEditMode={false} />;
        case 'CourseInfoWidget':
          return <CourseInfoWidget key={`${widget.id}-${widget.courseId}`} data={widget} isEditMode={false} />;
        case 'InfiniteBanner':
          return <InfiniteBanner {...commonProps} key={widget.id} />;
        case 'QAWidget':
          return <QAWidget {...commonProps} key={widget.id} />;
        case 'HolisticTestimonial':
          return <HolisticTestimonial {...commonProps} key={widget.id} />;
        case 'BragBar':
          return <BragBar {...commonProps} key={widget.id} />;
        case 'SpiritualFeatureBanner':
          return <SpiritualFeatureBanner {...commonProps} key={widget.id} />;
        case 'FancyFooter':
          return <FancyFooter {...commonProps} key={widget.id} pageData={pageData} />;
        case 'AchievementsBanner':
          return <AchievementsBanner {...commonProps} key={widget.id} />;
        case 'SpiritualQuoteCard':
          return <SpiritualQuoteCard {...commonProps} key={widget.id} />;
        case 'GalleryWidget':
          return <GalleryWidget {...commonProps} key={widget.id} />;
   
        default:
        return <div>Unbekanntes Widget</div>;
    }
  };

  const pageBackgroundColor = pageData?.backgroundColor || '#FFFFFF'; // Standardfarbe Weiß, wenn keine Hintergrundfarbe angegeben ist

  return (
    <div className="page_public" style={{ backgroundColor: pageBackgroundColor }}>
      
      {/* ✅ Modern Navigation System 2025 */}
      {pageData?.navigation?.enabled && (
        <NewNavCreator
          navigationData={pageData.navigation || {}}
          onUpdate={() => {}} // No editing on live page
          isEditMode={false}
          isMobileView={isMobileView}
          colors={pageData.colors || {}}
        />
      )}
      
      
      <div className="page_public_widgets_wrapper">
        {pageData.widgets.map((widget, index) => (
          <div key={index} className="widget-boss-wrapper" data-widget-id={widget.id} style={{ backgroundColor: pageBackgroundColor }}>
            {renderWidget(widget)}
          </div>
        ))}
        {pageData.whatsappStatus && (
          <img
            className="whatsapp-button-live"
            src="/img/whatsapp.svg"
            alt="WhatsApp"
            onClick={() => openWhatsApp(pageData.whatsappNumber)}
            style={{ cursor: 'pointer' }}
          />
        )}
      </div>
      
      {/* Popup Manager für Product Form Popups */}
      <PopupManager 
        products={products}
        isEditMode={false}
        pageId={currentPageId}
        onSubmitForm={async (formData, productData) => {
          console.log('🚀 PagePublic: onSubmitForm called');
          console.log('🚀 PagePublic: Form data received:', formData);
          console.log('🚀 PagePublic: Product data received:', productData);
          console.log('🚀 PagePublic: Current pageData:', pageData);
          
          try {
            // Get current user ID from page data (the page owner)
            const pageOwnerId = pageData?.userId;
            console.log('🔍 PagePublic: Page owner ID:', pageOwnerId);
            
            if (!pageOwnerId) {
              console.error('❌ PagePublic: Page owner not found in pageData');
              throw new Error('Page owner not found');
            }

            // Save customer data to the purchases collection (where ProductCustomers looks)
            const customerData = {
              customerEmail: formData.email,
              customerInfo: {
                name: formData.name,
                ...formData // Include any other form fields
              },
              productId: productData.id,
              productTitle: productData.title,
              amount: productData.price || 0,
              currency: productData.currency || 'EUR',
              paymentType: 'free', // Since this is for free products
              paymentStatus: 'completed',
              purchaseDate: new Date().toISOString(),
              status: 'active',
              source: 'product-form-popup',
              pageRoute: customRoute,
              userId: pageOwnerId,
              providerId: pageOwnerId  // ← Firebase Rules erwarten 'providerId' statt 'userId'
            };

            // Save to the purchases collection (where ProductCustomers component looks)
            console.log('💾 PagePublic: Attempting to save customer data:', customerData);
            console.log('💾 PagePublic: Database reference:', db);
            console.log('💾 PagePublic: Database URL:', db.app.options.databaseURL);
            console.log('💾 PagePublic: Auth user:', auth?.currentUser?.uid);
            
            const purchasesRef = ref(db, 'purchases');
            console.log('💾 PagePublic: Purchases ref created:', purchasesRef.toString());
            
            const newPurchaseRef = push(purchasesRef);
            console.log('💾 PagePublic: New purchase ref created with key:', newPurchaseRef.key);
            
            console.log('💾 PagePublic: About to call set() with data...');
            const setResult = await set(newPurchaseRef, customerData);
            console.log('💾 PagePublic: set() completed, result:', setResult);

            console.log('✅ PagePublic: Customer data saved successfully to purchases/', newPurchaseRef.key);
            console.log('✅ PagePublic: Full path: purchases/' + newPurchaseRef.key);
            
            alert(`Thank you ${formData.name}! Your information has been submitted for ${productData.title}.\n\nDebug Info:\n- Database: ${db.app.options.databaseURL}\n- Path: purchases/${newPurchaseRef.key}\n- Product: ${productData.title}`);
            
          } catch (error) {
            console.error('❌ PagePublic: Error saving customer data:', error);
            console.error('❌ PagePublic: Error code:', error.code);
            console.error('❌ PagePublic: Error message:', error.message);
            console.error('❌ PagePublic: Error details:', error.details);
            console.error('❌ PagePublic: Full error object:', JSON.stringify(error, null, 2));
            
            alert(`Error processing your request: ${error.message}\n\nError code: ${error.code}\n\nPlease try again or contact support.`);
            throw error; // Re-throw to prevent popup from closing
          }
        }}
      />
    </div>
  );
}

export default PagePublic;

